/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import { ClipboardList, Landmark, Plus, Search, Tag, X } from "lucide-react";
import {
  addAlias,
  addInsurer,
  insuranceGaps,
  insurers as loadInsurers,
  mapTag,
  removeAlias,
  setInNetwork,
  unmapTag,
  type Alias,
  type Insurer,
  type InsuranceGaps,
  type InsurerAdded,
  type PlanGap,
  type RefreshOutcome,
  type TagGap,
  type TagMapping,
} from "../api/insurance";
import { newIdempotencyKey, type ApiResult } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Badge } from "../components/ui/Badge";
import { BusyLabel } from "../components/ui/BusyLabel";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { GuardedButton } from "../components/ui/GuardedButton";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { ToggleButton } from "../components/ui/ToggleButton";
import { StateSelect } from "../components/StateSelect";
import { debounce } from "../lib/utils";

const EDIT_HINT = "Only care coordinators and admins can change insurance mappings.";
const PROVIDERS_SHOWN = 3;
const NETWORK_OPTIONS = [
  { value: "true", label: "In network" },
  { value: "false", label: "Not in network" },
];
const INSURER_OPTIONS = [
  { value: "existing", label: "Existing insurer" },
  { value: "new", label: "New insurer" },
];

type MapTarget = { kind: "tag"; gap: TagGap } | { kind: "plan"; gap: PlanGap };

type Dialog =
  | MapTarget
  | { kind: "addPlan"; insurer: Insurer }
  | { kind: "network"; insurer: Insurer }
  | { kind: "removeTag"; tag: TagMapping }
  | { kind: "removePlan"; alias: Alias };

/**
 * The translation between Healthie's vocabularies and our insurer list, owned by care
 * coordinators: provider PI tags and family plans that resolve to no insurer, and every insurer
 * with what is mapped to it. A plan mapping applies to families at once; a tag mapping applies on
 * the agent's next run.
 */
export function Insurance() {
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const canEdit = hasRole("Admin") || hasRole("CareCoordinator");
  const [gaps, setGaps] = useState<InsuranceGaps | null>(null);
  const [list, setList] = useState<Insurer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const [draft, setDraft] = useState(q);

  useChrome({}, []);

  // one debounce for the component's lifetime; recreating it per render would drop pending calls
  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParams((current) => withQuery(current, value), { replace: true });
      }, 300),
    [setSearchParams],
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  function showInsurer(insurer: Insurer) {
    applySearch.cancel();
    setDialog(null);
    setDraft(insurer.payer.cmsPayerId);
    setSearchParams((current) => withQuery(current, insurer.payer.cmsPayerId), { replace: true });
  }

  const load = useCallback(async () => {
    const [gapsResult, insurersResult] = await Promise.all([insuranceGaps(), loadInsurers()]);
    if (!gapsResult.ok) {
      setError(gapsResult.error.message);
      return;
    }
    if (!insurersResult.ok) {
      setError(insurersResult.error.message);
      return;
    }
    setError(null);
    setGaps(gapsResult.value);
    setList(insurersResult.value);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function done(message: string) {
    setDialog(null);
    showToast("success", message);
    void load();
  }

  async function turnOn(insurer: Insurer) {
    const result = await setInNetwork(insurer.payer.id, true);
    if (result.ok) {
      done(`${insurer.payer.displayName} is in network.`);
    } else {
      showToast("error", result.error.message);
    }
  }

  const needsMapping = gaps === null ? 0 : gaps.tags.length + gaps.plans.length;
  const needle = q.trim().toLowerCase();
  const tagGaps = (gaps?.tags ?? []).filter((gap) =>
    matches(needle, gap.tagName, ...gap.providers),
  );
  const planGaps = (gaps?.plans ?? []).filter((gap) =>
    matches(needle, gap.planId, gap.payerName, gap.rawPayerId),
  );
  const shownInsurers = (list ?? []).filter((insurer) =>
    matches(
      needle,
      insurer.payer.displayName,
      insurer.payer.cmsPayerId,
      ...insurer.tags.map((tag) => tag.tagName),
      ...healthiePlans(insurer).flatMap((alias) => [alias.rawString, insurer.planNames[alias.rawString] ?? null]),
    ),
  );

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Insurance
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          The insurer and state behind each provider tag and family insurance plan in Healthie.
          Healthie tags still decide which provider takes which insurer. A plan you map applies to
          families right away; a tag you map applies from the agent&apos;s next run.
        </p>
      </header>

      <div className="enter" style={{ "--enter-delay": "80ms" } as CSSProperties}>
        <Input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            applySearch(event.target.value);
          }}
          placeholder="Search by tag, plan, payer id, insurer, or provider"
          aria-label="Search insurance mappings"
          prefix={<Search size={15} className="text-text-tertiary" />}
          className="sm:max-w-xl"
        />
      </div>

      <ErrorMessage message={error} className="mb-0" />

      {(gaps === null || list === null) && error === null && (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      )}

      {gaps !== null && list !== null && (
        <>
          <section aria-labelledby="needs-mapping" className="space-y-4">
            <h2 id="needs-mapping" className="flex items-center gap-2 text-lg font-medium text-text-primary">
              Needs mapping
              <Badge tone={needsMapping > 0 ? "warning" : "success"}>{needsMapping}</Badge>
            </h2>
            {needsMapping === 0 ? (
              <EmptyState
                icon={<Landmark size={20} />}
                title="Everything is mapped"
                description="Every provider tag and family plan has an insurer."
              />
            ) : tagGaps.length + planGaps.length === 0 ? (
              <p className="text-sm text-text-tertiary">
                Nothing that needs mapping matches “{q.trim()}”.
              </p>
            ) : (
              <ul className="space-y-3">
                {tagGaps.map((gap) => (
                  <GapRow
                    key={`tag:${gap.tagName}`}
                    title={gap.tagName}
                    kind="Provider tag"
                    detail={providersCarrying(gap.providers)}
                    canEdit={canEdit}
                    onMap={() => setDialog({ kind: "tag", gap })}
                  />
                ))}
                {planGaps.map((gap) => (
                  <GapRow
                    key={`plan:${gap.planId}`}
                    title={gap.payerName ?? `Plan ${gap.planId}`}
                    kind={`Family plan ${gap.planId}${gap.rawPayerId === null ? "" : ` · payer id ${gap.rawPayerId}`}`}
                    detail={`${families(gap.families)}, ${gap.poolFamilies} in the agent's pool · ${gap.states.join(", ") || "no state"}`}
                    canEdit={canEdit}
                    onMap={() => setDialog({ kind: "plan", gap })}
                  />
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="insurers" className="space-y-4">
            <h2 id="insurers" className="text-lg font-medium text-text-primary">
              Insurers
            </h2>
            {shownInsurers.length === 0 && (
              <p className="text-sm text-text-tertiary">No insurer matches “{q.trim()}”.</p>
            )}
            <ul className="space-y-3">
              {shownInsurers.map((insurer) => (
                <InsurerRow
                  key={insurer.payer.id}
                  insurer={insurer}
                  canEdit={canEdit}
                  onNetwork={() =>
                    insurer.payer.inNetwork
                      ? setDialog({ kind: "network", insurer })
                      : void turnOn(insurer)
                  }
                  onAddPlan={() => setDialog({ kind: "addPlan", insurer })}
                  onRemoveTag={(tag) => setDialog({ kind: "removeTag", tag })}
                  onRemovePlan={(alias) => setDialog({ kind: "removePlan", alias })}
                />
              ))}
            </ul>
          </section>

          <MapDialog
            dialog={dialog?.kind === "tag" || dialog?.kind === "plan" ? dialog : null}
            insurers={list}
            plans={gaps.plans}
            onClose={() => setDialog(null)}
            onDone={done}
            onShowInsurer={showInsurer}
          />
          <AddPlanDialog
            insurer={dialog?.kind === "addPlan" ? dialog.insurer : null}
            onClose={() => setDialog(null)}
            onDone={done}
          />
          <ConfirmDialog
            confirmation={confirmationFor(dialog)}
            onClose={() => setDialog(null)}
            onDone={done}
          />
        </>
      )}
    </div>
  );
}

type Confirmation = {
  title: string;
  confirmLabel: string;
  body: string;
  run: () => Promise<Outcome>;
};

/** The confirmation an open network, tag or alias change asks for; null for any other dialog. */
function confirmationFor(dialog: Dialog | null): Confirmation | null {
  switch (dialog?.kind) {
    case "network": {
      const { payer, poolFamilies } = dialog.insurer;
      return {
        title: "Mark out of network",
        confirmLabel: "Mark out of network",
        body: `${families(poolFamilies)} in the agent's pool ${poolFamilies === 1 ? "has" : "have"} ${payer.displayName}. Out of network, they stay on file, but the agent offers them to no provider, including providers tagged for ${payer.displayName}.`,
        run: async () =>
          outcome(await setInNetwork(payer.id, false), `${payer.displayName} is out of network.`),
      };
    }
    case "removeTag": {
      const { tagName } = dialog.tag;
      return {
        title: "Remove tag mapping",
        confirmLabel: "Remove",
        body: `From the agent's next run, providers tagged ${tagName} are no longer matched on it, and the tag goes back to Needs mapping.`,
        run: async () => outcome(await unmapTag(tagName), `${tagName} is unmapped.`),
      };
    }
    case "removePlan": {
      const { alias } = dialog;
      return {
        title: "Remove Healthie plan",
        confirmLabel: "Remove",
        body: `Plan ${alias.rawString} stops meaning this insurer. Families on it are matched again right away, through another mapping for the plan or the plan's payer id. Families neither one matches lose their insurer.`,
        run: async () => {
          const result = await removeAlias(alias.id);
          return outcome(
            result,
            `Plan ${alias.rawString} removed.${result.ok ? refreshNote(result.value.refreshed, "changed insurer", "changed insurer") : ""}`,
          );
        },
      };
    }
    default:
      return null;
  }
}

function withQuery(current: URLSearchParams, value: string): URLSearchParams {
  const next = new URLSearchParams(current);
  if (value.trim() === "") {
    next.delete("q");
  } else {
    next.set("q", value.trim());
  }
  return next;
}

function matches(needle: string, ...values: (string | null)[]): boolean {
  return (
    needle === "" ||
    values.some((value) => value !== null && value.toLowerCase().includes(needle))
  );
}

function providersCarrying(providers: string[]): string {
  const count = providers.length === 1 ? "1 provider" : `${providers.length} providers`;
  const named = providers.slice(0, PROVIDERS_SHOWN).join(", ");
  const rest = providers.length - PROVIDERS_SHOWN;
  return rest > 0 ? `${count}: ${named} and ${rest} more` : `${count}: ${named}`;
}

function outcome(result: ApiResult<unknown>, success: string): Outcome {
  return result.ok ? { ok: true, message: success } : { ok: false, message: result.error.message };
}

function families(count: number): string {
  return count === 1 ? "1 family" : `${count} families`;
}

/** The only aliases that set a family's insurer; the other sources' rows are read by nothing yet. */
function healthiePlans(insurer: Insurer): Alias[] {
  return insurer.aliases.filter((alias) => alias.source === "HEALTHIE");
}

function planLabel(alias: Alias, planNames: Record<string, string>): string {
  const name = planNames[alias.rawString];
  const plan = name === undefined ? `Plan ${alias.rawString}` : `${name} (plan ${alias.rawString})`;
  return alias.state === null ? plan : `${plan}, ${alias.state} only`;
}

/** How many clients a mapping change moved, as "1 client is now on X" or "2 clients are now on X". */
function refreshNote(refreshed: RefreshOutcome | null, one: string, many: string): string {
  if (refreshed === null) {
    return "";
  }
  if (refreshed.changed === 0) {
    return " No client changed insurer.";
  }
  return refreshed.changed === 1 ? ` 1 client ${one}.` : ` ${refreshed.changed} clients ${many}.`;
}

function resolvesTo(refreshed: RefreshOutcome | null, name: string): string {
  return refreshNote(refreshed, `is now on ${name}`, `are now on ${name}`);
}

function combined(first: RefreshOutcome | null, second: RefreshOutcome | null): RefreshOutcome | null {
  if (first === null || second === null) {
    return first ?? second;
  }
  return {
    examined: first.examined + second.examined,
    changed: first.changed + second.changed,
    failed: first.failed + second.failed,
  };
}

/** Nexus caches an answered failure and replays it to the same key, so only a lost response keeps its key. */
function answered(result: ApiResult<unknown>): boolean {
  return !result.ok && result.error.httpStatus > 0;
}

function GapRow({
  title,
  kind,
  detail,
  canEdit,
  onMap,
}: {
  title: string;
  kind: string;
  detail: string;
  canEdit: boolean;
  onMap: () => void;
}) {
  return (
    <li className="border border-border bg-bg-elevated shadow-card flex items-center justify-between gap-4 rounded-2xl p-4">
      <div className="min-w-0">
        <p className="truncate font-medium text-text-primary">{title}</p>
        <p className="text-xs text-text-tertiary">{kind}</p>
        <p className="mt-1 text-sm text-text-secondary">{detail}</p>
      </div>
      <GuardedButton
        locked={!canEdit}
        hint={EDIT_HINT}
        variant="primary"
        ariaLabel={`Map ${title}`}
        onClick={onMap}
      >
        Map
      </GuardedButton>
    </li>
  );
}

function InsurerRow({
  insurer,
  canEdit,
  onNetwork,
  onAddPlan,
  onRemoveTag,
  onRemovePlan,
}: {
  insurer: Insurer;
  canEdit: boolean;
  onNetwork: () => void;
  onAddPlan: () => void;
  onRemoveTag: (tag: TagMapping) => void;
  onRemovePlan: (alias: Alias) => void;
}) {
  const { payer } = insurer;
  return (
    <li className="border border-border bg-bg-elevated shadow-card space-y-3 rounded-2xl p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-text-primary">{payer.displayName}</p>
          <p className="text-xs text-text-tertiary">payer id {payer.cmsPayerId}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={payer.inNetwork ? "success" : "neutral"}>
            {payer.inNetwork ? "In network" : "Out of network"}
          </Badge>
          <GuardedButton
            locked={!canEdit}
            hint={EDIT_HINT}
            variant="ghost"
            ariaLabel={`${payer.inNetwork ? "Mark out of network" : "Mark in network"}: ${payer.displayName}`}
            onClick={onNetwork}
          >
            {payer.inNetwork ? "Mark out of network" : "Mark in network"}
          </GuardedButton>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {insurer.tags.map((tag) => (
          <Chip
            key={tag.tagName}
            icon={<Tag size={10} />}
            label={`${tag.tagName} → ${tag.state}`}
            removeLabel={canEdit ? `Remove ${tag.tagName}` : null}
            onRemove={() => onRemoveTag(tag)}
          />
        ))}
        {healthiePlans(insurer).map((alias) => (
          <Chip
            key={alias.id}
            icon={<ClipboardList size={10} />}
            label={planLabel(alias, insurer.planNames)}
            removeLabel={canEdit ? `Remove plan ${alias.rawString}` : null}
            onRemove={() => onRemovePlan(alias)}
          />
        ))}
        <GuardedButton
          locked={!canEdit}
          hint={EDIT_HINT}
          variant="ghost"
          ariaLabel={`Add Healthie plan to ${payer.displayName}`}
          onClick={onAddPlan}
        >
          <Plus size={12} className="mr-1 inline-block" />
          Add Healthie plan
        </GuardedButton>
      </div>
    </li>
  );
}

function Chip({
  icon,
  label,
  removeLabel,
  onRemove,
}: {
  icon?: ReactNode;
  label: string;
  removeLabel: string | null;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-bg-muted px-2 py-0.5 text-text-secondary">
      {icon}
      {label}
      {removeLabel !== null && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className="text-text-tertiary hover:text-error"
        >
          <X size={10} />
        </button>
      )}
    </span>
  );
}

/** A choice nobody has made yet stays null, so a required choice cannot be skipped. */
function NetworkChoice({
  value,
  onChange,
}: {
  value: boolean | null;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-text-secondary">Network</p>
      <ToggleButton
        className="h-9"
        ariaLabel="Network"
        options={NETWORK_OPTIONS}
        value={value === null ? "" : String(value)}
        onChange={(chosen) => onChange(chosen === "true")}
      />
    </div>
  );
}

/**
 * Maps a tag or a plan to an insurer, adding the insurer first when it is new. Each step keeps its
 * own idempotency key while its inputs are unchanged, so a retry after a half-done save replays the
 * insurer nexus already created instead of refusing it as a duplicate.
 *
 * A new insurer's payer id also resolves every unmapped plan that carries it as its raw payer id,
 * so the dialog names those plans before the save.
 */
function MapDialog({
  dialog,
  insurers,
  plans,
  onClose,
  onDone,
  onShowInsurer,
}: {
  dialog: MapTarget | null;
  insurers: Insurer[];
  plans: PlanGap[];
  onClose: () => void;
  onDone: (message: string) => void;
  onShowInsurer: (insurer: Insurer) => void;
}) {
  const [mode, setMode] = useState("existing");
  const [choice, setChoice] = useState("");
  const [name, setName] = useState("");
  const [payerId, setPayerId] = useState("");
  const [inNetwork, setInNetworkChoice] = useState<boolean | null>(null);
  const [state, setState] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<Insurer | null>(null);
  const [insurerKey, setInsurerKey] = useState(newIdempotencyKey);
  const [mappingKey, setMappingKey] = useState(newIdempotencyKey);

  useEffect(() => {
    setMode("existing");
    setChoice("");
    setError(null);
    setExisting(null);
    if (dialog?.kind === "tag") {
      setName("");
      setPayerId("");
      setInNetworkChoice(true);
      setState(dialog.gap.suggestedState ?? "");
    } else if (dialog?.kind === "plan") {
      setName(dialog.gap.payerName ?? "");
      setPayerId(dialog.gap.rawPayerId ?? "");
      setInNetworkChoice(null);
      setState("");
    }
  }, [dialog]);

  // nexus refuses a key it already saw with a different body, so a changed form is a new write.
  useEffect(() => {
    setInsurerKey(newIdempotencyKey());
  }, [dialog, name, payerId, inNetwork]);
  useEffect(() => {
    setMappingKey(newIdempotencyKey());
  }, [dialog, mode, choice, name, payerId, inNetwork, state]);

  const adding = mode === "new";
  const newPayerId = payerId.trim();
  const capturedPlans =
    adding && newPayerId !== ""
      ? plans.filter(
          (plan) =>
            plan.rawPayerId?.trim() === newPayerId &&
            !(dialog?.kind === "plan" && plan.planId === dialog.gap.planId),
        )
      : [];
  const picked = adding ? undefined : insurers.find((insurer) => insurer.payer.id === choice);
  const ready =
    (picked !== undefined ||
      (adding && name.trim() !== "" && payerId.trim() !== "" && inNetwork !== null)) &&
    (dialog?.kind !== "tag" || state !== "");

  async function insurerFor(): Promise<ApiResult<InsurerAdded>> {
    if (picked !== undefined) {
      return { ok: true, value: { payer: picked.payer, refreshed: null } };
    }
    const added = await addInsurer(
      { displayName: name.trim(), payerId: newPayerId, inNetwork: inNetwork === true },
      insurerKey,
    );
    if (answered(added)) {
      setInsurerKey(newIdempotencyKey());
    }
    return added;
  }

  async function save(target: MapTarget) {
    setSaving(true);
    setError(null);
    setExisting(null);
    const insurer = await insurerFor();
    if (!insurer.ok) {
      setSaving(false);
      if (insurer.error.httpStatus === 409) {
        setExisting(insurers.find((known) => known.payer.cmsPayerId === newPayerId) ?? null);
      }
      setError(insurer.error.message);
      return;
    }
    const { payer, refreshed } = insurer.value;
    const insurerName = payer.displayName;
    const mapped =
      target.kind === "tag"
        ? await mapTag({ tagName: target.gap.tagName, payerId: payer.id, state }, mappingKey).then(
            (result) =>
              withKeyRotated(
                result,
                `${target.gap.tagName} grants ${insurerName} in ${state}. It applies on the agent's next run.${resolvesTo(refreshed, insurerName)}`,
              ),
          )
        : await addAlias(
            { payerId: payer.id, source: "HEALTHIE", rawString: target.gap.planId, state: null },
            mappingKey,
          ).then((result) =>
            withKeyRotated(
              result,
              `Plan ${target.gap.planId} means ${insurerName}.${result.ok ? resolvesTo(combined(refreshed, result.value.refreshed), insurerName) : ""}`,
            ),
          );
    setSaving(false);
    if (mapped.ok) {
      onDone(mapped.message);
    } else {
      setError(mapped.message);
    }
  }

  function withKeyRotated(result: ApiResult<unknown>, success: string): Outcome {
    if (answered(result)) {
      setMappingKey(newIdempotencyKey());
    }
    return outcome(result, success);
  }

  const title = dialog?.kind === "tag" ? `Map ${dialog.gap.tagName}` : dialog?.kind === "plan" ? `Map plan ${dialog.gap.planId}` : "";

  return (
    <Modal isOpen={dialog !== null} onClose={onClose} title={title} size="md">
      <div className="space-y-4 px-6 py-5">
        <ToggleButton
          className="h-9"
          ariaLabel="Existing or new insurer"
          options={INSURER_OPTIONS}
          value={mode}
          onChange={setMode}
        />
        {adding ? (
          <div className="space-y-3">
            <Input label="Insurer name" value={name} onChange={(event) => setName(event.target.value)} />
            <Input
              label="Payer id"
              value={payerId}
              onChange={(event) => setPayerId(event.target.value)}
            />
            <p className="text-xs text-text-tertiary">
              The claims payer id, as Healthie's plan catalog or the insurance card shows it.
            </p>
            <NetworkChoice value={inNetwork} onChange={setInNetworkChoice} />
            {capturedPlans.length > 0 && (
              <p className="rounded-lg border border-warning-border bg-warning-bg px-3 py-2 text-sm text-text-primary">
                Payer id {newPayerId} is also on{" "}
                {capturedPlans.length === 1 ? "1 other plan" : `${capturedPlans.length} other plans`} (
                {capturedPlans.map((plan) => plan.payerName ?? `plan ${plan.planId}`).join(", ")}), so{" "}
                {families(capturedPlans.reduce((sum, plan) => sum + plan.families, 0))} on{" "}
                {capturedPlans.length === 1 ? "it" : "them"} will move to this insurer too. To keep
                them separate, use a different payer id or choose an existing insurer.
              </p>
            )}
          </div>
        ) : (
          <Select
            label="Insurer"
            value={choice}
            onChange={setChoice}
            searchable
            placeholder="Choose an insurer…"
            options={insurers.map((insurer) => ({
              value: insurer.payer.id,
              label: `${insurer.payer.displayName} (${insurer.payer.cmsPayerId})`,
            }))}
          />
        )}
        {dialog?.kind === "tag" && (
          <StateSelect label="State the tag grants" value={state} onChange={setState} />
        )}
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
            {existing !== null && (
              <>
                {" "}
                <button type="button" onClick={() => onShowInsurer(existing)} className="underline">
                  Show {existing.payer.displayName}
                </button>
              </>
            )}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready || saving}
            onClick={() => dialog !== null && void save(dialog)}
          >
            <BusyLabel busy={saving} busyText="Saving…">
              Save mapping
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function AddPlanDialog({
  insurer,
  onClose,
  onDone,
}: {
  insurer: Insurer | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [planId, setPlanId] = useState("");
  const [state, setState] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [key, setKey] = useState(newIdempotencyKey);

  useEffect(() => {
    setPlanId("");
    setState("");
    setError(null);
  }, [insurer]);

  // nexus refuses a key it already saw with a different body, so a changed form is a new write.
  useEffect(() => {
    setKey(newIdempotencyKey());
  }, [insurer, planId, state]);

  async function save(target: Insurer) {
    setSaving(true);
    setError(null);
    const result = await addAlias(
      { payerId: target.payer.id, source: "HEALTHIE", rawString: planId.trim(), state: state === "" ? null : state },
      key,
    );
    setSaving(false);
    if (answered(result)) {
      setKey(newIdempotencyKey());
    }
    if (result.ok) {
      onDone(
        `Plan ${planId.trim()} means ${target.payer.displayName}.${resolvesTo(result.value.refreshed, target.payer.displayName)}`,
      );
    } else {
      setError(result.error.message);
    }
  }

  return (
    <Modal
      isOpen={insurer !== null}
      onClose={onClose}
      title={insurer === null ? "" : `Add a Healthie plan to ${insurer.payer.displayName}`}
      size="md"
    >
      <div className="space-y-4 px-6 py-5">
        <Input
          label="Healthie plan id"
          value={planId}
          onChange={(event) => setPlanId(event.target.value)}
        />
        <StateSelect label="Only for families in" value={state} onChange={setState} allowNone />
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={planId.trim() === "" || saving}
            onClick={() => insurer !== null && void save(insurer)}
          >
            <BusyLabel busy={saving} busyText="Saving…">
              Add plan
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type Outcome = { ok: boolean; message: string };

/** A confirmed action: success closes it with the message, failure keeps it open and says why. */
function ConfirmDialog({
  confirmation,
  onClose,
  onDone,
}: {
  confirmation: Confirmation | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = confirmation !== null;

  useEffect(() => {
    setError(null);
  }, [open]);

  return (
    <Modal isOpen={open} onClose={onClose} title={confirmation?.title ?? ""} size="sm">
      {confirmation !== null && (
        <div className="space-y-4 px-6 py-5">
          <p className="text-sm leading-relaxed text-text-secondary">{confirmation.body}</p>
          {error !== null && (
            <p role="alert" className="text-sm font-medium text-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saving}
              onClick={() => {
                setSaving(true);
                void confirmation.run().then((outcome) => {
                  setSaving(false);
                  if (outcome.ok) {
                    onDone(outcome.message);
                  } else {
                    setError(outcome.message);
                  }
                });
              }}
            >
              <BusyLabel busy={saving} busyText="Saving…">
                {confirmation.confirmLabel}
              </BusyLabel>
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
